import { fetchApi } from '@/app/api';
import {
  CatalogContentRow,
  DTO,
  parseCatalogContentRowDto,
} from '@certhon/models';

/**
 * Fetch the entire docs catalog content for a project as a flat list of
 * heterogeneous rows (groups + filetokens). The frontend reconstructs the
 * tree from `parent_group_id`.
 *
 * Returns [] when the project has no published catalog.
 */
export async function fetchCatalogContentByProject(
  projectId: number,
): Promise<CatalogContentRow[]> {
  const rows = (await fetchApi('/catalog_content', {
    project_id: `eq.${projectId}`,
  })) as DTO<CatalogContentRow>[];
  return rows.map(parseCatalogContentRowDto);
}

/**
 * Returns true if the current user is subscribed to email notifications for
 * the catalog. Uses an RPC that derives the user id from request.jwt.claims,
 * so the client can't query subscriptions for other users.
 */
export async function getCatalogSubscription(
  catalogId: number,
): Promise<boolean> {
  const res = await rpc('get_catalog_subscription', {
    p_catalog_id: catalogId,
  });
  return res === true;
}

export async function subscribeCatalog(catalogId: number): Promise<void> {
  await rpc('subscribe_catalog', { p_catalog_id: catalogId });
}

export async function unsubscribeCatalog(catalogId: number): Promise<void> {
  await rpc('unsubscribe_catalog', { p_catalog_id: catalogId });
}

// PostgREST RPC call. POST /rpc/<fn> with JSON body of named args; returns
// the function's result body. Scalar-returning functions yield the bare
// scalar; void functions yield no body.
async function rpc(
  fn: string,
  args: Record<string, unknown>,
): Promise<unknown> {
  const url = new URL(
    `/api/rpc/${fn}`,
    `${window.location.protocol}//${window.location.host}`,
  );
  const res = await fetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      message = body?.message || body?.error || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  // Some RPCs return void → empty body; others return a scalar.
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
